import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { styled } from '@linaria/react'
import { minimalVars } from '@ovasabi/ui-minimal/tokens'
import { MinimalAlert, MinimalBadge, MinimalButton, MinimalCard } from '@ovasabi/ui-minimal'
import {
  PageHeader,
  PageHeaderActionButton,
  Screen,
  SectionHeading,
  WeeklyScheduleEditor,
} from '../../components/chow'
import { useVendorProfileSnapshot } from '../../generated/prototypeRuntime'
import { commandPayload, commands, isLiveRuntime } from '../../lib/runtime'
import {
  defaultSchedule,
  effectiveSchedule,
  isValidSchedule,
  parseSchedule,
  rememberSavedSchedule,
  toSchedulePayload,
  type WeeklySchedule,
} from '../../lib/vendorSchedule'
import { confirmedUpsert, type StoreRecord } from './chefShared'
import { useChooseChow, usePageContext } from './ChooseChowContext'

const Style = {
  Intro: styled.p`
    margin: 0;
    color: ${minimalVars.color.textSecondary};
    font-size: ${minimalVars.typography.captionSize};
    line-height: ${minimalVars.typography.lineHeightBody};
  `,
  CardHeader: styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${minimalVars.space.sm};
    margin-bottom: ${minimalVars.space.xs};
  `,
  /* Two quiet actions, sized to their labels and parked at the trailing edge —
     a save bar, not a pair of hero buttons. */
  Actions: styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${minimalVars.space.xs};
  `,
}

export const ChefSchedulePage = () => {
  const navigate = useNavigate()
  const page = usePageContext('/schedule')
  const { profileId } = useChooseChow()
  const vendors = useVendorProfileSnapshot() as { records: readonly StoreRecord[] }
  const record = useMemo(
    () =>
      vendors.records.find((vendor) =>
        isLiveRuntime() ? String(vendor.profile_id ?? '') === profileId : true,
      ),
    [vendors.records, profileId],
  )
  const stored = useMemo(() => parseSchedule(record?.schedule), [record])
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<'saved' | 'error' | ''>('')
  /**
   * What to show: the projection when it carries this vendor's week, otherwise
   * the last write we confirmed.
   *
   * In live mode the write goes to the command lane and comes back on the
   * `vendor_profiles` projection, which can lag by a sweep — and if the vendor
   * row has not been projected into this scope at all, `stored` is null
   * indefinitely. Holding the confirmed week in component state was not enough:
   * it died with the component, so leaving for the Kitchen and coming back
   * showed the Mon–Sat 9–5 default again, which is what "saving does nothing"
   * looks like. `effectiveSchedule` keeps the hold across mounts and reloads and
   * drops it the moment the projection agrees, so a change made elsewhere still
   * wins as soon as it lands.
   *
   * Recomputed per render (not memoised on `stored` alone) so a save made on
   * this page is picked up without waiting for a projection tick.
   */
  const baseline = effectiveSchedule(profileId, stored, record?.updated_at)
  const [draft, setDraft] = useState<WeeklySchedule>(() => baseline ?? defaultSchedule())
  const baselineKey = baseline ? JSON.stringify(toSchedulePayload(baseline)) : ''

  useEffect(() => {
    if (!dirty) {
      setDraft(baseline ?? defaultSchedule())
    }
    // `baselineKey` is the value identity of `baseline`; depending on the object
    // itself would restamp the draft on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baselineKey, dirty])

  const updateDraft = (next: WeeklySchedule) => {
    setDraft(next)
    setDirty(true)
    setNotice('')
  }

  const reset = () => {
    setDraft(baseline ?? defaultSchedule())
    setDirty(false)
    setNotice('')
  }

  const save = async () => {
    if (!isValidSchedule(draft) || saving) return
    setSaving(true)
    setNotice('')
    const payload = toSchedulePayload(draft)
    try {
      let confirmed: StoreRecord | null = null
      if (isLiveRuntime()) {
        // The command answers with the persisted vendor row (profile service
        // `vendorMap`), which carries the row's real id — the one the projection
        // will key on. Merging it beats inventing a `vendor-<profileId>` record:
        // that synthetic id never matches the projected row, so the store ended
        // up describing the same kitchen twice.
        const saved = commandPayload(await commands.profile.setVendorSchedule(profileId, payload))
        if (saved.id) {
          confirmed = saved as StoreRecord
        }
      } else {
        void commands.profile.setVendorSchedule(profileId, payload).catch(() => undefined)
      }
      // Reflect the saved hours immediately in the shared vendor store so the
      // Kitchen dashboard's Working-hours card updates now, not a projection
      // sweep later. Runs in live too (confirmedUpsert), because the awaited
      // command above already succeeded — echoUpsert would no-op in live and the
      // card would keep showing the old week ("hours don't update after saving").
      confirmedUpsert('profile.vendor_profiles', {
        // No invented `is_open`: the server row defaults closed, and claiming
        // open here is what made the Kitchen sign flip to Offline a sweep later.
        ...(record ?? { id: `vendor-${profileId}`, profile_id: profileId }),
        ...(confirmed ?? {}),
        schedule: payload,
      })
      // Hold what we saved so every chef surface — this page after a
      // navigation, the Kitchen card, a reload — shows the week that was
      // actually written until the projection carries it.
      rememberSavedSchedule(profileId, draft)
      setDirty(false)
      setNotice('saved')
    } catch {
      setNotice('error')
    } finally {
      setSaving(false)
    }
  }

  const unchanged = !dirty && baseline !== null

  return (
    <Screen
      header={(
        <PageHeader
          title={page.title}
          accentDot
          action={(
            <PageHeaderActionButton size="sm" onClick={() => navigate('/dashboard')}>
              Kitchen
            </PageHeaderActionButton>
          )}
        />
      )}
    >
      <Style.Intro>
        Your regular opening hours — customers see these when they order or schedule ahead.
      </Style.Intro>

      {notice === 'saved' ? (
        <MinimalAlert tone="success" title="Working hours saved">
          Your kitchen schedule is ready for new and scheduled orders.
        </MinimalAlert>
      ) : notice === 'error' ? (
        <MinimalAlert tone="danger" title="Hours could not be saved">
          Check your connection and try again. Your changes are still on this page.
        </MinimalAlert>
      ) : null}

      <MinimalCard padding="sm" enter={false}>
        <Style.CardHeader>
          <SectionHeading title="Regular week" />
          <MinimalBadge tone="info" size="sm">{draft.timezone || 'Africa/Lagos'}</MinimalBadge>
        </Style.CardHeader>
        <WeeklyScheduleEditor value={draft} onChange={updateDraft} disabled={saving} />
      </MinimalCard>

      <Style.Actions>
        <MinimalButton size="sm" variant="ghost" onClick={reset} disabled={saving || unchanged}>
          Reset
        </MinimalButton>
        <MinimalButton
          size="sm"
          onClick={save}
          loading={saving}
          disabled={!isValidSchedule(draft) || unchanged}
        >
          Save hours
        </MinimalButton>
      </Style.Actions>
    </Screen>
  )
}
